import React from 'react';
import styled, { withTheme, css } from 'styled-components';
import { prop, ifProp } from 'styled-tools';

import { observer } from 'mobx-react';
import media from 'themes/media';
import sizes from 'themes/sizes';
import Spinner from 'Components/Spinner';

import { ReactComponent as Close } from 'common/assets/icons/common/signs/white-close-sign.svg';
import { ReactComponent as Edit } from '../../common/assets/icons/common/actions/blue-edit.svg';
import { ReactComponent as Info } from '../../common/assets/icons/common/signs/white-info.svg';
import { ReactComponent as Down } from '../../common/assets/icons/common/arrows/pointers/black-down-arrow-pointer.svg';
import { ReactComponent as Up } from '../../common/assets/icons/common/arrows/pointers/black-top-arrow-pointer.svg';
import { ReactComponent as Warning } from '../../common/assets/icons/success-failure-warn/warn/warning.svg';
import { ReactComponent as OK } from '../../common/assets/icons/success-failure-warn/success/white-thick-check.svg';
import { ReactComponent as Card } from '../../common/assets/icons/common/wallet/white-wallet.svg';

const colors = (props) => ({
  secondary: {
    background: 'transparent',
    border: '#2d9cdb',
    color: '#2d9cdb',
  },
  primary: {
    background: '#2D9CDB',
    border: '#2d9cdb',
    color: 'white',
  },
  link: {
    background: 'transparent',
    border: 'transparent',
    color: props.theme.colors.background.buttonLinkColor,
  },
  default: {
    background: 'white',
    border: props.theme.colors.background.borderColor,
    color: '#2d9cdb',
  },
  accent: {
    background: '#FF9248',
    border: props.theme.colors.background.borderColor,
    color: 'white',
  },
  tertiary: {
    background: props.theme.utils.tertiary,
    border: props.theme.utils.tertiary,
    color: 'white',
  },
});

const getColor = (props) => colors(props)[props.type] || colors(props).default;

const ButtonWrapper = styled.button`
  ${sizes};
  transition: all 0.3s ease-in-out;
  border-radius: 4px;
  display: ${(props) => (props.inline ? 'inline-flex' : 'flex')};
  flex-wrap: nowrap;
  justify-content: center;
  align-items: center;
  padding: 0 18px;
  cursor: pointer;
  color: ${(props) => getColor(props).color};
  height: 34px;
  background: ${(props) => getColor(props).background};
  letter-spacing: 1px;
  text-transform: uppercase;
  font-size: 12px;
  font-weight: 500;
  width: ${(props) => (props.inline ? 'auto' : '100%')};
  border: 1px solid ${(props) => getColor(props).border};
  cursor: ${(props) => (props.disabled ? 'auto' : 'pointers')};
  opacity: ${(props) => (props.disabled ? 0.5 : 1)};
  max-width: ${prop('maxWidth')};

  svg {
    fill: ${(props) => getColor(props).color};
    display: block;
  }

  :hover {
  }

  ${media.greaterThan('tablet')`
    height: ${(props) => (props.alwaysSmall ? '34px' : '42px')};
  `}

  ${ifProp(
    'isDisona',
    css`
      border-radius: 40px;
      width: 150px;
      padding: 5px 15px;
      font-weight: 400;
      font-size: 10px;
      line-height: 14px;
      height: 30px;
      &[type='primary'] {
        background: #00f47f;
        color: #001947;
        height: 30px;
        font-size: 10px;
        font-family: 'Founders Grotesk Mono';
      }
      &[type='secondary'] {
        background: #215988;
        color: #ffffff;
        height: 30px;
        font-size: 10px;
        font-family: 'Founders Grotesk Mono';
      }
      &[type='link'] {
        background: #215988;
        color: #ffffff;
        height: 30px;
        font-size: 10px;
        font-family: 'Founders Grotesk Mono';
      }
      width: ${ifProp('isMobile', '140px', '150px')};
      ${media.lessThan('tablet')`
        width: 130px;
        &[type='primary'] {
          font-size: 8px;
        }
        &[type='secondary'] {
          font-size: 8px;
        }
        &[type='link'] {
          font-size: 8px;
        }
      `}
    `
  )}
`;

const Icon = styled.div`
  margin-right: ${(props) => (props.onlyIcon ? 0 : '8px')};
`;

const SpinnerContainer = styled.div`
  position: relative;
  width: 20px;
  margin-right: 8px;
`;

const icons = {
  edit: <Edit />,
  info: <Info />,
  close: <Close />,
  down: <Down />,
  up: <Up />,
  warning: <Warning />,
  ok: <OK />,
  card: <Card />,
};

const Button = ({
  icon,
  children,
  isLoading,
  disabled,
  onClick,
  className,
  ...otherProps
}) => (
  <ButtonWrapper
    disabled={disabled}
    onClick={(e) => {
      if (!disabled && onClick) {
        onClick(e);
      }
    }}
    className={className}
    {...otherProps}
  >
    {isLoading && (
      <SpinnerContainer>
        <Spinner size={20} withoutMargin color={getColor(otherProps).color} />
      </SpinnerContainer>
    )}
    {!isLoading && icon && (
      <Icon onlyIcon={children == null}>{icons[icon]}</Icon>
    )}

    {children}
  </ButtonWrapper>
);

export default observer(withTheme(Button));
